import axios from "axios";

/**
 * Low-level video download via your backend API.
 * - Reports progress as 0..100 when total is known; otherwise null while downloading.
 * - Retries on failure; throws after retries exhausted (so caller can mark ❌).
 */
const onClickDownloadVideo = async (
  downloadFileName,
  downloadFileNameExtension,
  fileurl,
  onProgress,             // function(percent:number|null)
  retryCount = 3,
  abortSignal             // optional AbortSignal
) => {
  const apiurl = process.env.REACT_APP_VIDEO_GETBLOB_API_URL;

  // Derive blobName safely from fileurl
  let blobName = "";
  try {
    const u = new URL(fileurl);
    const parts = u.pathname.split("/");
    blobName = parts[parts.length - 1] || "";
  } catch {
    const parts = String(fileurl || "").split("/");
    blobName = parts[parts.length - 1] || "";
  }

  const fileExtension = (downloadFileNameExtension || "").replace(/^\./, "");

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  try {
    const response = await axios.get(apiurl, {
      params: { blobName, fileExtension },
      responseType: "blob",
      signal: abortSignal, // axios v1 supports AbortSignal
      onDownloadProgress: (evt) => {
        // total can be missing if server doesn't expose Content-Length or CORS doesn't expose it
        const total = typeof evt.total === "number" && evt.total > 0 ? evt.total : null;
        if (total) {
          const percentage = Math.round((evt.loaded / total) * 100);
          if (typeof onProgress === "function") onProgress(percentage);
        } else {
          // indicate "in progress" but unknown percent
          if (typeof onProgress === "function") onProgress(null);
        }
      },
      validateStatus: (status) => status >= 200 && status < 300, // fail on non-2xx
    });

    // Build filename (keep your existing behavior)
    let finalName = downloadFileName || "video";
    const hasDot = /\.[a-z0-9]+$/i.test(finalName);
    if (!hasDot && fileExtension) {
      finalName = `${finalName}.${fileExtension}`;
    }

    const blob = new Blob([response.data], {
      type: response.headers?.["content-type"] || "application/octet-stream",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);

    // ensure final 100 when total was unknown
    if (typeof onProgress === "function") onProgress(100);
  } catch (error) {
    if (abortSignal?.aborted) {
      // surface as-is so caller can treat as cancelled
      throw error instanceof Error ? error : new Error("Download aborted");
    }

    if (retryCount > 0) {
      console.warn(
        `onClickDownloadVideo: error (${error?.message || error}). Retrying... attempts left: ${retryCount}`
      );
      await delay(1000);
      return onClickDownloadVideo(
        downloadFileName,
        downloadFileNameExtension,
        fileurl,
        onProgress,
        retryCount - 1,
        abortSignal
      );
    }

    // Exhausted retries → throw so caller can mark ❌ Failed
    const finalErr = error instanceof Error ? error : new Error(String(error || "Download failed"));
    console.error("onClickDownloadVideo: all retry attempts failed.", finalErr);
    throw finalErr;
  }
};

/**
 * UI handler that:
 * - Normalizes the filename
 * - Calls onClickDownloadVideo
 * - Updates your downloadProgress store keyed by finalImageURL
 *
 * NOTE: This version writes an OBJECT with {status, percent} so your Sidebar shows ❌ correctly.
 */
const handleDownloadClick = async (
  e,
  fileName,
  fileExtension,
  finalImageURL,
  setDownloadProgress,
  abortSignal // optional
) => {
  e.preventDefault();

  // Split the file name and extension (keep your original logic)
  const [baseName, ext] = fileName.split(".").reduce(
    (acc, part, index, arr) => {
      if (index === arr.length - 1) {
        acc[1] = part;
      } else {
        acc[0] += (index ? "." : "") + part;
      }
      return acc;
    },
    ["", ""]
  );

  // If provided fileName already ends with the same extension, use baseName (avoid duplicate .ext)
  const finalFileName = ext === fileExtension ? baseName : fileName;

  // Mark started
  setDownloadProgress((prev) => ({
    ...prev,
    [finalImageURL]: { status: "in-progress", percent: 0 },
  }));

  try {
    await onClickDownloadVideo(
      finalFileName,
      fileExtension,
      finalImageURL,
      (progress) => {
        // progress can be a number or null (unknown total)
        setDownloadProgress((prev) => ({
          ...prev,
          [finalImageURL]:
            progress == null
              ? { status: "in-progress" } // no percent when unknown
              : {
                  status: progress === 100 ? "completed" : "in-progress",
                  percent: progress,
                },
        }));
      },
      3,
      abortSignal
    );

    // final guard: ensure completed if onProgress didn't send 100
    setDownloadProgress((prev) => ({
      ...prev,
      [finalImageURL]: { status: "completed", percent: 100 },
    }));
  } catch (err) {
    // Mark ❌ Failed so Sidebar can render failure state
    setDownloadProgress((prev) => ({
      ...prev,
      [finalImageURL]: {
        status: "failed",
        error: err?.message || "Download failed",
      },
    }));
  }
};

export default handleDownloadClick;